import React, { useState, useRef } from "react";
import "./staff-modules.css";

const SAMPLE_BATCHES = ["GNM 1st Year", "GNM 2nd Year", "GNM 3rd Year"];
const SAMPLE_DEPARTMENTS = [
  "General Ward", "Pediatrics", "ICU", "Community Health",
  "Nursing Foundation", "Med Surg", "RPH", "UPH", "CHN",
  "Vacation", "Exam", "Theory Block", "Clinical Block"
];
const DEPARTMENT_COLORS = {
  "General Ward": "#e1bee7",
  "Pediatrics": "#b2dfdb",
  "ICU": "#ffcdd2",
  "Community Health": "#fff9c4",
  "Nursing Foundation": "#b3e5fc",
  "Med Surg": "#ffe0b2",
  "RPH": "#ffe082",
  "UPH": "#c8e6c9",
  "CHN": "#dcf8c6",
  "Vacation": "#f8bbd0",
  "Exam": "#bbdefb",
  "Theory Block": "#cfd8dc",
  "Clinical Block": "#f0f4c3"
};

const DEFAULT_ROTATIONS = [
  {
    id: 1,
    batch: "GNM 1st Year",
    department: "General Ward",
    from: "2025-09-01",
    to: "2025-09-15"
  },
  {
    id: 2,
    batch: "GNM 2nd Year",
    department: "Pediatrics",
    from: "2025-09-01",
    to: "2025-09-15"
  }
];

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  return [d.getDate().toString().padStart(2, "0"), (d.getMonth()+1).toString().padStart(2, "0"), d.getFullYear()].join("-");
}

export default function Rotation() {
  const [rotations, setRotations] = useState(DEFAULT_ROTATIONS);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    batch: SAMPLE_BATCHES[0],
    department: SAMPLE_DEPARTMENTS[0],
    from: "",
    to: ""
  });
  const [message, setMessage] = useState("");
  const printRef = useRef();

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMessage("");
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setRotations([
      { ...form, id: Date.now() },
      ...rotations
    ]);
    setForm({
      batch: SAMPLE_BATCHES[0],
      department: SAMPLE_DEPARTMENTS[0],
      from: "",
      to: ""
    });
    setShowForm(false);
    setMessage("Rotation plan added successfully!");
  };

  // Print/download handler (prints only the rotation table)
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Rotation Plan</title>
          <style>
            body { font-family: Arial, sans-serif; }
            .rotation-table { border-collapse: collapse; width: 100%; margin-top: 10px;}
            .rotation-table th, .rotation-table td { border: 1px solid #333; padding: 6px 10px; text-align: center; }
            .rotation-table th { background: #e3f2fd; }
            .rotation-legend { margin-top: 18px; font-size: 14px; }
            .legend-item { display: inline-block; margin-right: 16px; }
            .legend-color { display: inline-block; width: 18px; height: 18px; vertical-align: middle; margin-right: 6px; border: 1px solid #333; }
            h2, h3 { text-align: center; margin: 0.4em 0; }
          </style>
        </head>
        <body>
          ${printContents}
          <div style="margin-top: 36px; font-size:16px; width:100%;">
            <div style="float:left; margin-left:2em;">Sign of Co-ordinator</div>
            <div style="float:right; margin-right:2em;">Sign of Principal</div>
          </div>
        </body>
      </html>
    `);
    setTimeout(() => {
      win.print();
      win.close();
    }, 350);
  };

  return (
    <div className="module-wrapper">
      <h2>Rotation Plan</h2>
      <p>
        Manage and schedule rotation plans for students and staff. Assign batches to departments or clinical areas for specific time periods, and ensure compliance with GNM curriculum requirements.
      </p>
      <div className="module-actions">
        <button className="primary-action" onClick={() => setShowForm(s => !s)}>
          {showForm ? "Cancel" : "Create Rotation Plan"}
        </button>
      </div>
      {showForm && (
        <form className="rotation-form" onSubmit={handleFormSubmit}>
          <div className="form-row">
            <label>Batch:</label>
            <select name="batch" value={form.batch} onChange={handleInputChange}>
              {SAMPLE_BATCHES.map((b) => (
                <option key={b}>{b}</option>
              ))}
            </select>
            <label>Department:</label>
            <select name="department" value={form.department} onChange={handleInputChange}>
              {SAMPLE_DEPARTMENTS.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
            <label>From:</label>
            <input name="from" type="date" value={form.from} onChange={handleInputChange} required />
            <label>To:</label>
            <input name="to" type="date" value={form.to} onChange={handleInputChange} required />
          </div>
          <button type="submit" className="primary-action">Add Rotation</button>
        </form>
      )}
      {message && <div className="form-success">{message}</div>}
      <div className="rotation-list" ref={printRef}>
        <h3 style={{marginBottom:0}}>MASTER ROTATION PLAN</h3>
        <div style={{textAlign:"center", fontWeight:600, fontSize:"1.1em", margin:"0.5em 0"}}>
          {/* You can make this dynamic: */}
          SAROJINI COLLEGE OF NURSING, DHULE.<br/>
          GNM ROTATION PLAN {new Date().getFullYear()} - {new Date().getFullYear()+1}
        </div>
        <table className="rotation-table">
          <thead>
            <tr>
              <th>Batch</th>
              <th>Department</th>
              <th>From</th>
              <th>To</th>
              <th>Duration (days)</th>
            </tr>
          </thead>
          <tbody>
            {rotations.map((r) => {
              const fromDate = new Date(r.from);
              const toDate = new Date(r.to);
              const duration = r.from && r.to
                ? Math.max(1, Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1)
                : "-";
              return (
                <tr key={r.id}>
                  <td>{r.batch}</td>
                  <td>
                    <span
                      style={{
                        display: "inline-block",
                        background: DEPARTMENT_COLORS[r.department] || "#eee",
                        minWidth: "96px",
                        borderRadius: "4px",
                        padding: "2px 6px"
                      }}
                    >
                      {r.department}
                    </span>
                  </td>
                  <td>{formatDate(r.from)}</td>
                  <td>{formatDate(r.to)}</td>
                  <td>{duration}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Legend */}
        <div className="rotation-legend">
          <div><b>Legend:</b></div>
          {Object.entries(DEPARTMENT_COLORS).map(([dept, color]) =>
            <span className="legend-item" key={dept}>
              <span className="legend-color" style={{background: color}}></span>
              {dept}
            </span>
          )}
        </div>
      </div>
      <button
        className="secondary-action"
        style={{ marginTop: 18 }}
        onClick={handlePrint}
      >
        Download/Print Rotation Plan
      </button>
    </div>
  );
}